import React, { useEffect, useState } from 'react';
import SafetyNote from './SafetyNote';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  BookmarkAdd,
  BookmarkAdded,
  Explore,
  Favorite,
  FavoriteBorder,
  FmdGood,
  LinkOff,
  Map as MapIcon,
  Public,
  Route,
  ScubaDiving,
  TravelExplore,
} from '@mui/icons-material';
import AppNavigation from './AppNavigation';
import DiveSiteMap from './DiveSiteMap';
import SiteCommunity from './SiteCommunity';
import { Dive } from '../types/Dive';
import { diveService } from '../services/diveService';
import { authService } from '../services/authService';
import { buildDiveSiteMatches } from '../utils/diveSiteMatcher';
import { useDiveSitePreferences } from '../hooks/useDiveSitePreferences';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';

const formatVisitDate = (date: string): string => {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDiveTitle = (dive: Dive): string => {
  return dive.diveName?.trim() || dive.location || `Dive on ${formatVisitDate(dive.startTime)}`;
};

const DiveSiteDetail: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [dives, setDives] = useState<Dive[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { allSites, loading: catalogLoading, error: catalogError } = useDiveSiteCatalog();
  const {
    preferences: sitePreferences,
    loading: preferencesLoading,
    saving: preferencesSaving,
    error: preferencesError,
    isFavorite,
    isWishlisted,
    isDiveSiteMatchIgnored,
    toggleFavorite,
    toggleWishlist,
    setDiveSiteMatchIgnored,
  } = useDiveSitePreferences();
  const site = allSites.find((entry) => entry.slug === slug);

  useEffect(() => {
    // Guests never had dives to load — skip the call rather than let it 401 and
    // get misread as an expired session.
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }

    const loadDives = async () => {
      try {
        setLoading(true);
        setError(null);
        const diveData = await diveService.getMyDives();
        setDives(diveData);
      } catch (err: any) {
        console.error('Failed to load dives for dive site detail page:', err);
        if (err.message?.includes('Unauthorized')) {
          setError('Your session has expired. Browse the destination details below, or log in again to see your dive history here.');
        } else {
          setError('Unable to load your dives right now. You can still browse the destination details.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadDives();
  }, []);

  if (catalogLoading && !site) {
    return (
      <Box>
        <AppNavigation />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">Loading dive site details...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!site) {
    return (
      <Box>
        <AppNavigation />
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/dive-sites')}>
              Back to catalog
            </Button>
          }
        >
          That dive site could not be found in the current catalog.
        </Alert>
      </Box>
    );
  }
  const siteMatches = buildDiveSiteMatches(dives, allSites, sitePreferences.ignoredDiveSiteMatches);
  const match = siteMatches.get(site.id);
  const ignoredMatches = dives
    .filter((dive) => isDiveSiteMatchIgnored(dive.id, site.id))
    .sort((left, right) => new Date(right.startTime).getTime() - new Date(left.startTime).getTime());
  const favorite = isFavorite(site.id);
  const wishlisted = isWishlisted(site.id);
  const relatedSites = allSites
    .filter((entry) => entry.id !== site.id && (entry.country === site.country || entry.region === site.region))
    .slice(0, 3);

  return (
    <Box>
      <AppNavigation />

      <Button startIcon={<ArrowBack />} onClick={() => navigate('/dive-sites')} sx={{ mb: 2 }}>
        Back to dive sites
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: 3 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
              {site.name}
            </Typography>
            {match ? (
              <Chip color="success" label={`Dove ${match.diveCount}x`} />
            ) : site.featured ? (
              <Chip color="primary" variant="outlined" label="Featured destination" />
            ) : null}
            {site.source === 'community' ? <Chip color="info" variant="outlined" label="Community added" /> : null}
            {favorite ? <Chip color="warning" label="Favorite" /> : null}
            {wishlisted ? <Chip color="primary" variant="outlined" label="Want to dive" /> : null}
          </Stack>
          <Typography variant="body1" color="text.secondary">
            {site.region}, {site.country}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 780, mt: 1.25 }}>
            {site.summary}
          </Typography>
          {site.source === 'community' && site.submittedByName ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Added by {site.submittedByName}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Button
              variant={favorite ? 'contained' : 'outlined'}
              color="warning"
              startIcon={favorite ? <Favorite /> : <FavoriteBorder />}
              onClick={() => toggleFavorite(site.id)}
            >
              {favorite ? 'Saved as favorite' : 'Save as favorite'}
            </Button>
            <Button
              variant={wishlisted ? 'contained' : 'outlined'}
              startIcon={wishlisted ? <BookmarkAdded /> : <BookmarkAdd />}
              onClick={() => toggleWishlist(site.id)}
            >
              {wishlisted ? 'On your wishlist' : 'Want to dive this'}
            </Button>
          </Stack>
        </Box>

        <Paper sx={{ p: 2, minWidth: { xs: '100%', md: 260 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
            Match snapshot
          </Typography>
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Checking your dive history...
              </Typography>
            </Stack>
          ) : match ? (
            <Stack spacing={0.75}>
              <Typography variant="body2" color="text.secondary">
                Last dive {formatVisitDate(match.lastVisited)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Match source: {match.matchType === 'coordinates' ? 'GPS proximity' : 'location text'}
              </Typography>
              {match.nearestDistanceKm != null && (
                <Typography variant="body2" color="text.secondary">
                  Nearest logged dive: {match.nearestDistanceKm.toFixed(1)} km
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No logged dives currently match this catalog destination.
            </Typography>
          )}
          {(preferencesLoading || preferencesSaving) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Syncing saved lists...
            </Typography>
          )}
        </Paper>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {preferencesError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {preferencesError}
        </Alert>
      )}

      {catalogError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {catalogError}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
              <Public color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Region
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {site.region}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {site.country}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
              <Explore color="info" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Dive style
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
              {site.siteType.replace('-', ' ')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Difficulty: {site.difficulty}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
              <FmdGood color="success" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Catalog point
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {site.latitude.toFixed(3)}, {site.longitude.toFixed(3)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Match radius: {site.matchRadiusKm} km
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <MapIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Site map
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {match ? <Chip size="small" color="success" label="Dove" /> : null}
            {favorite ? <Chip size="small" color="warning" label="Favorite" /> : null}
            {wishlisted ? <Chip size="small" color="primary" variant="outlined" label="Want to dive" /> : null}
          </Stack>
        </Stack>
        <DiveSiteMap sites={[site]} siteMatches={siteMatches} preferences={sitePreferences} height={320} dialogTitle={`${site.name} map`} />
      </Paper>

      <SafetyNote kind="siteReference" sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Why it stands out
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              {site.tags.map((tag) => (
                <Chip key={tag} size="small" variant="outlined" label={tag} />
              ))}
            </Stack>
            <Stack spacing={1.25}>
              {site.highlights.map((highlight) => (
                <Paper key={highlight} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">{highlight}</Typography>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
              <ScubaDiving color="info" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Your dive history here
              </Typography>
            </Stack>

            {loading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : match ? (
              <Stack spacing={1.5}>
                {match.matchedDives.map((dive) => (
                  <Card key={dive.id} variant="outlined">
                    <CardContent>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {formatDiveTitle(dive)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatVisitDate(dive.startTime)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {dive.location || 'No location label saved'}
                          </Typography>
                        </Box>
                        <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1} alignItems={{ xs: 'center', sm: 'flex-end' }}>
                          <Button component={RouterLink} to={`/dive/${dive.id}`} size="small" variant="outlined">
                            Open dive
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<LinkOff />}
                            onClick={() => setDiveSiteMatchIgnored(dive.id, site.id, true)}
                          >
                            Not this site
                          </Button>
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip size="small" icon={<Route />} label={`${dive.maxDepth.toFixed(1)} m max depth`} />
                        <Chip size="small" label={dive.diveType} />
                        {dive.description ? <Chip size="small" variant="outlined" label="Has notes" /> : null}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">
                No dives are currently mapped to this site. As the catalog expands, this page will pick up more region and sub-site matches.
              </Alert>
            )}

            {ignoredMatches.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Ignored matches for this site
                </Typography>
                <Stack spacing={1.5}>
                  {ignoredMatches.map((dive) => (
                    <Card key={`ignored-${dive.id}`} variant="outlined" sx={{ borderStyle: 'dashed' }}>
                      <CardContent>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {formatDiveTitle(dive)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatVisitDate(dive.startTime)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Marked as not this site
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setDiveSiteMatchIgnored(dive.id, site.id, false)}
                          >
                            Undo
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <SiteCommunity site={site} />

      {relatedSites.length > 0 && (
        <Paper sx={{ p: 2.5, mt: 3 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
            <TravelExplore color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Nearby catalog picks
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            {relatedSites.map((relatedSite) => (
              <Grid item xs={12} md={4} key={relatedSite.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {relatedSite.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {relatedSite.summary}
                    </Typography>
                    <Button component={RouterLink} to={`/dive-sites/${relatedSite.slug}`} size="small">
                      View site
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default DiveSiteDetail;